import { environment } from 'src/environments/environment';
import { InjectionToken, Signal } from '@angular/core';
import type {
  Notification,
  NotificationSummary,
  NotificationFilter,
  NotificationGrouping,
  AddNotificationRequest,
  GetNotificationsRequest,
  GetNotificationsResponse,
  NotificationResponse
} from '@shared/definitions/notification.interface';



/**
 * Pattern B contract for user Notifications.
 *
 * User-scoped. Auto-syncs via workspace change in the Firestore adapter.
 */
export interface INotificationsStore {
  readonly notifications: Signal<readonly Notification[]>;
  readonly summary: Signal<NotificationSummary | null>;
  readonly loading: Signal<boolean>;
  readonly lastError: Signal<Error | null>;

  readonly totalCount: Signal<number>;
  readonly unreadCount: Signal<number>;
  readonly urgentCount: Signal<number>;
  readonly unread: Signal<readonly Notification[]>;
  readonly urgent: Signal<readonly Notification[]>;

  refresh(): Promise<void>;

  addNotification(request: AddNotificationRequest): Promise<NotificationResponse>;
  markAsRead(notificationId: string): Promise<NotificationResponse>;
  markAllAsRead(): Promise<NotificationResponse>;
  remove(notificationId: string): Promise<NotificationResponse>;

  fetch(request?: GetNotificationsRequest): Promise<GetNotificationsResponse>;
  cleanupExpired(): Promise<NotificationResponse>;
  /** Dev-only utility — creates N test notifications for QA. */
  createTestNotifications(count?: number): Promise<NotificationResponse>;

  filter(filter: NotificationFilter): readonly Notification[];
  grouped(): NotificationGrouping;
}

export const NOTIFICATIONS_STORE = new InjectionToken<INotificationsStore>('NOTIFICATIONS_STORE');

export type NotificationsStoreMode = 'mock' | 'firestore';

export function resolveNotificationsStoreMode(): NotificationsStoreMode {
  if (typeof window === 'undefined') return 'firestore';
  if (environment.production) return 'firestore'; // QA-only mock store: never honour ?store=mock in production (silent data loss).
  const url = new URL(window.location.href);
  const param = url.searchParams.get('store');
  if (param === 'firestore' || param === 'mock') {
    try {
      window.localStorage.setItem('notifications.storeMode', param);
    } catch {
      /* ignore */
    }
    return param;
  }
  try {
    const stored = window.localStorage.getItem('notifications.storeMode');
    if (stored === 'firestore' || stored === 'mock') return stored;
  } catch {
    /* ignore */
  }
  return 'firestore';
}
